import React, { useState, useEffect, useRef } from 'react';
import Modal from 'react-bootstrap/Modal';
import Form from 'react-bootstrap/Form';
import InputGroup from 'react-bootstrap/InputGroup';
import axios from 'axios';
import { FaRegTrashCan, FaArrowLeft } from "react-icons/fa6";
import { HiOutlinePlus } from "react-icons/hi";
import { TbEditCircle } from "react-icons/tb";
import { FiSave, FiXCircle, FiCheck, FiX, FiSend } from "react-icons/fi";
import { toast } from "react-toastify";
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

function EmailTemplates({ show, onHide }) {
  const [loading, setLoading] = useState(false);
  const [testLoading, setTestLoading] = useState(false);
  const [emailTemplates, setEmailTemplates] = useState([]);
  const [newTemplate, setNewTemplate] = useState(null);
  const [editTemplate, setEditTemplate] = useState(null);
  const [testEmailRecipient, setTestEmailRecipient] = useState('');
  const [tournaments, setTournaments] = useState([]);
  const [selectedTournamentId, setSelectedTournamentId] = useState('');
  const quillEditRef = useRef(null);

  const participantPlaceholders = [
    { placeholder: '{{firstname}}', alias: 'Vorname' },
    { placeholder: '{{lastname}}', alias: 'Nachname' },
    { placeholder: '{{teamname}}', alias: 'Teamname' },
    { placeholder: '{{paid}}', alias: 'Bezahlstatus' },
  ];
  const tournamentPlaceholders = [
    { placeholder: '{{t_name}}', alias: 'Turniername' },
    { placeholder: '{{t_location}}', alias: 'Ort' },
    { placeholder: '{{t_time}}', alias: 'hh:mm Uhr' },
    { placeholder: '{{t_time1}}', alias: 'hh:mm' },
    { placeholder: '{{t_prize}}', alias: 'Gebühr' },
    { placeholder: '{{t_info}}', alias: 'Infotext' },
    { placeholder: '{{t_link}}', alias: 'Link(Button)' },
    { placeholder: '{{t_signup}}', alias: 'Anmelde-Button' },
    { placeholder: '{{t_players}}', alias: 'Spieler' },
    { placeholder: '{{t_teams}}', alias: 'Teams' },
    { placeholder: '{{t_fieldplayers}}', alias: 'Feldpieler' },
    { placeholder: '{{t_date}}', alias: 'DD.MM.YYYY' },
    { placeholder: '{{t_date1}}', alias: 'dd.Monat JJJJ' },
    { placeholder: '{{t_date2}}', alias: 'Tag, dd.Monat JJJJ' },
  ];

  const fetchEmailTemplates = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`/api/EmailTemplate`);
      setEmailTemplates(response.data.data || []);
    } catch (error) {
      console.error("Fehler beim Laden der Templates:", error);
      toast.error("Fehler beim Laden der Email-Templates");
      setEmailTemplates([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchTournaments = async () => {
    try {
      const response = await axios.get('/tournament');
      // Sortiere Turniere nach Datum (neueste zuerst)
      const sorted = (response.data.data || []).sort((a, b) => new Date(b.date) - new Date(a.date));
      setTournaments(sorted);
    } catch (error) {
      console.error("Fehler beim Laden der Turniere:", error);
      // Kein Toast hier, um den User nicht zu nerven, wenn er nur Templates bearbeiten will
    }
  };

  const handleChangeQuillInEdit = (value) => {
    if (editTemplate) {
      setEditTemplate(prev => ({ ...prev, content: value }));
    }
  };
  const handleChangeQuillInNew = (value) => {
    if (newTemplate) {
      setNewTemplate(prev => ({ ...prev, content: value }));
    }
  };

  useEffect(() => {
    if (show) {
      fetchEmailTemplates();
      fetchTournaments();
    } else {
      setNewTemplate(null);
      setEditTemplate(null);
      setTestEmailRecipient(''); // Test-Empfänger auch zurücksetzen
      setSelectedTournamentId(''); // Turnier-Auswahl zurücksetzen
    }
  }, [show]);

  const handleAddNewRow = () => {
    if (!newTemplate) {
      setNewTemplate({ title: '', subject: '', content: '', deletable: true });
      setEditTemplate(null);
    }
  };

  const handleInputChange = (field, value) => {
    if (newTemplate) {
      setNewTemplate({ ...newTemplate, [field]: value });
    } else if (editTemplate) {
      setEditTemplate({ ...editTemplate, [field]: value });
    }
  };

  const handleSaveNewTemplate = async () => {
    if (!newTemplate || !newTemplate.title) {
      toast.error("Titel ist erforderlich");
      return;
    }
    setLoading(true);
    try {
      const payload = {
        title: newTemplate.title,
        subject: newTemplate.subject || '',
        content: newTemplate.content || '',
        deletable: newTemplate.deletable === undefined ? true : newTemplate.deletable,
      };
      await axios.post(`/api/EmailTemplate`, payload);
      setNewTemplate(null);
      await fetchEmailTemplates();
      toast.success("Email-Vorlage erfolgreich gespeichert");
    } catch (error) {
      console.error("Fehler beim Speichern:", error);
      toast.error("Fehler beim Speichern der Email-Vorlage");
      setLoading(false);
    }
  };

  const handleDeleteTemplate = async (id) => {
    if (!window.confirm("Möchtest du diese Vorlage wirklich löschen?")) {
      return;
    }
    setLoading(true);
    try {
      await axios.delete(`/api/EmailTemplate/${id}`);
      await fetchEmailTemplates();
      toast.success("Email-Vorlage erfolgreich gelöscht");
    } catch (error) {
      console.error("Fehler beim Löschen:", error);
      toast.error("Fehler beim Löschen der Email-Vorlage");
      setLoading(false);
    }
  };

  const handleEditTemplate = (template) => {
    setEditTemplate({ ...template });
    setNewTemplate(null);
  };

  const handleSaveEditedTemplate = async () => {
    if (!editTemplate) return;
    setLoading(true);
    try {
      const { _id, deletable, ...updateData } = editTemplate;
      await axios.put(`/api/EmailTemplate/${_id}`, updateData);
      setEditTemplate(null);
      await fetchEmailTemplates();
      toast.success("Email-Vorlage erfolgreich aktualisiert");
    } catch (error) {
      console.error("Fehler beim Aktualisieren:", error);
      toast.error("Fehler beim Aktualisieren der Email-Vorlage");
      setLoading(false);
    }
  };

  const handleBackToOverview = () => {
    setEditTemplate(null);
  };

  const handleClose = () => {
    setNewTemplate(null);
    setEditTemplate(null);
    setEditTemplate(null);
    setTestEmailRecipient(''); // Test-Empfänger auch zurücksetzen
    setSelectedTournamentId(''); // Turnier-Auswahl zurücksetzen
    onHide();
  };

  const insertPlaceholder = (placeholder) => {
    const quill = quillEditRef.current?.getEditor();
    if (quill) {
      const range = quill.getSelection(true);
      if (range) {
        quill.insertText(range.index, placeholder, 'user');
        quill.setSelection(range.index + placeholder.length, 0, 'user');
      } else {
        // Fallback: Wenn keine Auswahl/Cursor da ist, am Ende einfügen
        const length = quill.getLength();
        quill.insertText(length, placeholder, 'user');
        quill.setSelection(length + placeholder.length, 0, 'user');
      }
      quill.focus();
    }
  };

  const renderPlaceholderButtons = (placeholders) => (
    <div className="d-flex flex-wrap justify-content-start align-items-center mb-2">
      {placeholders.map(({ placeholder, alias }) => (
        <button
          key={placeholder + alias}
          type="button"
          className="btn btn-outline-secondary btn-sm m-1 py-0 px-2"
          onClick={() => insertPlaceholder(placeholder)}
          title={`Platzhalter ${placeholder} einfügen`}
        >
          {alias}
        </button>
      ))}
    </div>
  );

  const handleSendTestEmail = async () => {
    if (!testEmailRecipient || !editTemplate) {
      toast.warn("Bitte geben Sie eine gültige E-Mail-Adresse für den Test ein.");
      return;
    }
    if (!/\S+@\S+\.\S+/.test(testEmailRecipient)) {
      toast.warn("Bitte geben Sie eine gültige E-Mail-Adresse ein.");
      return;
    }

    setTestLoading(true);
    try {
      const payload = {
        recipientEmail: testEmailRecipient,
        emailTemplate: {
          subject: editTemplate.subject || '',
          content: editTemplate.content || ''
        },
        tournamentId: selectedTournamentId || null // Turnier-ID mitsenden
      };
      await axios.post('/mail/send-test', payload);
      toast.success(`Test-E-Mail erfolgreich an ${testEmailRecipient} gesendet.`);

    } catch (error) {
      console.error("Fehler beim Senden der Test-E-Mail:", error);
      toast.error(error.response?.data?.message || "Fehler beim Senden der Test-E-Mail.");
    } finally {
      setTestLoading(false);
    }
  };


  return (
    <Modal size="lg" show={show} onHide={handleClose} centered className='no-carot'>
      <Modal.Header className='justify-content-between border border-0 rakete-rot' closeButton>
        {editTemplate ? (
          <>
            <FaArrowLeft size={20} className='hover icon-edit me-2' onClick={handleBackToOverview} title="Zurück zur Übersicht" style={{ cursor: 'pointer' }} />
            <Modal.Title className='rakete-font fw-semibold text-uppercase'>{editTemplate.title}</Modal.Title>
            <div style={{ width: '20px' }}></div>
          </>
        ) : (
          <Modal.Title className='rakete-font fw-semibold text-uppercase'>Email - Vorlagen</Modal.Title>
        )}
      </Modal.Header>
      <Modal.Body className='shadow'>
        <div className='mx-2'>
          {!editTemplate && (
            <p className="fs-7 mt-2 font-dark text-justify">
              Die Email-Vorlagen werden beim Verwalten eines Turniers verwendet...
            </p>
          )}
          <div className='fw-semibold text-center fs-6'>
            <div className="text-center">
              {!editTemplate ? (
                // --- ÜBERSICHT ---
                <>
                  <div className='row py-2 d-flex border-top border-start border-end rounded-top-4 align-items-center justify-content-center'>
                    <div className='col fs-7 text-start text-wrap fw-semibold'>Name</div>
                  </div>
                  {loading && !newTemplate && emailTemplates.length === 0 ? (
                    <div className='row py-3 d-flex bg-light text-secondary border-top border-start border-end align-items-center justify-content-center'> Lade Templates... </div>
                  ) : emailTemplates.length === 0 && !newTemplate ? (
                    <div className='row py-3 d-flex bg-light text-secondary border-top border-start border-end align-items-center justify-content-center'> Keine Email-Vorlagen vorhanden </div>
                  ) : (
                    emailTemplates.map((template) => (
                      template ? (
                        <div key={template._id} className={`row py-3 d-flex border-top border-start border-end align-items-center justify-content-center ${!template.deletable && 'bg-light'}`}>
                          <div className='col-8 fs-7 text-start text-wrap text-secondary'> {template.title || 'Unbekannt'} </div>
                          <div className='col-4 text-end'>
                            {template.deletable && (
                              <FaRegTrashCan size={20} onClick={() => handleDeleteTemplate(template._id)} title="Email-Vorlage löschen" className='hover icon-edit me-4' style={{ cursor: 'pointer' }} disabled={loading} />
                            )}
                            <TbEditCircle size={22} onClick={() => handleEditTemplate(template)} title="Email-Vorlage bearbeiten" className='hover icon-edit text-end' style={{ cursor: 'pointer' }} disabled={loading} />
                          </div>
                        </div>
                      ) : null
                    ))
                  )}
                  {newTemplate && (
                    <div className="row py-3 d-flex bg-light border-top border-start border-end align-items-center justify-content-center">
                      <div className='col-8 fs-7 text-start text-wrap text-secondary'>
                        <input type="text" value={newTemplate.title} onChange={(e) => handleInputChange('title', e.target.value)} placeholder="Template - Name" className="form-control form-control-sm" />
                      </div>
                      <div className='col-4 text-end'>
                        <FiX size={20} className='hover icon-edit me-3' onClick={() => setNewTemplate(null)} title="Eingabe abbrechen" style={{ cursor: 'pointer' }} />
                        <FiCheck size={20} className='hover icon-edit' onClick={handleSaveNewTemplate} title="Template speichern" style={{ cursor: 'pointer' }} />
                      </div>
                    </div>
                  )}
                  {!newTemplate && (
                    <div className='row py-2 d-flex border rounded-bottom-4 hover add align-items-center justify-content-center' onClick={handleAddNewRow} style={{ cursor: 'pointer' }}>
                      <HiOutlinePlus size={22} className='' title="Email-Vorlage hinzufügen" />
                    </div>
                  )}
                </>
              ) : (
                // --- BEARBEITUNGSMODUS ---
                <div className="text-start">
                  <div className='row py-2 d-flex border-top border-start border-end rounded-top-4 align-items-center justify-content-center'>
                    <div className='col fs-7 text-start text-wrap fw-semibold'>Bearbeiten: {editTemplate.title}</div>
                  </div>
                  <div className="row py-3 d-flex bg-light border-top border-start cursor border-end align-items-center justify-content-center">
                    <div className='col-12 fs-7 text-start text-wrap text-secondary'>
                      <label className="form-label fw-semibold">Betreff</label>
                      <input
                        type="text" value={editTemplate.subject || ''}
                        onChange={(e) => handleInputChange('subject', e.target.value)}
                        placeholder="Betreff" className="form-control form-control-sm mb-3"
                      />
                      <label className="form-label fw-semibold">Inhalt</label>
                      <div className='bg-white mb-3'>
                        <ReactQuill
                          ref={quillEditRef}
                          value={editTemplate.content || ''}
                          onChange={handleChangeQuillInEdit}
                          modules={{ toolbar: [[{ 'header': '1' }, { 'header': '2' }, { 'font': [] }], [{ 'list': 'ordered' }, { 'list': 'bullet' }], ['bold', 'italic', 'underline'], [{ 'align': [] }], ['link'], [{ 'color': [] }, { 'background': [] }], ['clean']] }}
                          placeholder="Schreibe hier..."
                        />
                      </div>

                      {/* Platzhalter-Buttons */}
                      <div className="mt-2 pt-2 border-top">
                        <h6 className="mt-2 mb-1 text-muted small fw-bold">Teilnehmer-Platzhalter:</h6>
                        {renderPlaceholderButtons(participantPlaceholders)}
                        <h6 className="mt-3 mb-1 text-muted small fw-bold">Turnier-Platzhalter:</h6>
                        {renderPlaceholderButtons(tournamentPlaceholders)}
                      </div>

                      {/* Test E-Mail Senden */}
                      <div className="mt-3 pt-3 border-top">
                        <label className="form-label fw-semibold">Test-E-Mail:</label>
                        <Form.Select
                          size="sm"
                          className="mb-2"
                          value={selectedTournamentId}
                          onChange={(e) => setSelectedTournamentId(e.target.value)}
                        >
                          <option value="">Beispiel-Turnier (Dummy Daten)</option>
                          {tournaments.map(t => (
                            <option key={t._id} value={t._id}>
                              {t.name} ({new Date(t.date).toLocaleDateString()})
                            </option>
                          ))}
                        </Form.Select>
                        <InputGroup size="sm">
                          <Form.Control
                            type="email"
                            placeholder="test@example.com"
                            value={testEmailRecipient}
                            onChange={(e) => setTestEmailRecipient(e.target.value)}
                            disabled={testLoading || loading} // Auch während Haupt-Laden deaktivieren
                          />
                          <button
                            className="text-center decline-button text-uppercase rounded-end fw-semibold fs-7 px-3"
                            type="button"
                            onClick={handleSendTestEmail}
                            disabled={!testEmailRecipient || testLoading || loading} // Deaktivieren
                            title="Aktuellen Inhalt als Test senden"
                          >
                            {testLoading ? 'Sende...' : <FiSend className="me-1" />}
                          </button>
                        </InputGroup>
                      </div>

                    </div>
                  </div>
                  <div className='row py-0 d-flex border rounded-bottom-4'></div>
                </div>
              )}
            </div>
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer className="border-0">
        {editTemplate ? (
          <>
            <button className="text-center decline-button text-uppercase rounded fw-semibold fs-7" type="button" onClick={handleBackToOverview} disabled={loading}>
              <FiXCircle className="me-1" /> Abbrechen
            </button>
            <button className="text-center login-button text-uppercase rounded fw-semibold border border-0 fs-7" type="button" onClick={handleSaveEditedTemplate} disabled={loading}>
              {loading ? 'SPEICHERT...' : <><FiSave className="me-1" /> Speichern</>}
            </button>
          </>
        ) : (
          <button className="text-center decline-button text-uppercase rounded fw-semibold fs-7" type="button" onClick={handleClose} disabled={loading}>
            <FiXCircle className="me-1" /> Schließen
          </button>
        )}
      </Modal.Footer>
    </Modal>
  );
}

export default EmailTemplates;
